import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { add, sub } from 'date-fns';

import { Card, ContractDetailsSkeleton, ContractDetailsSkeletonFullWidth, LoaderWrapper } from '@/components/common';
import { Calendar as CalendarSvg } from '@/assets/icons';
import { media } from '@/styles/theme';
import { classList, convertBlockToDate, formatDate } from '@/helpers';
import { useTimezone } from '@/hooks';
import { MAX_CONTRACT_DETAILS_WIDGET_HEIGHT, WEEK_DAY_TIME_FORMAT } from '@/config/variables';

interface IContractDetailsProps {
  contractDetails?: {
    clientName: string;
    jobName: string;
    contractName: string;
    snapshotBlock: number;
    weekStartBlock: number;
  } | null;
  fullWidth?: boolean;
  isLoading?: boolean;
}

const ContractDetails = ({
  contractDetails,
  fullWidth,
  isLoading = false,
}: IContractDetailsProps): React.ReactElement => {
  const { timezoneName } = useTimezone();
  const { t } = useTranslation();

  return (
    <StyledLoaderWrapper
      fullWidth={fullWidth}
      isLoading={isLoading}
      loader={fullWidth ? <ContractDetailsSkeletonFullWidth /> : <ContractDetailsSkeleton />}
    >
      <Container className={classList(fullWidth && 'full-width')}>
        <Card
          name={t`gigCompliance.gigDetails.title`}
          icon={CalendarSvg}
          maxBodyHeight={!fullWidth ? MAX_CONTRACT_DETAILS_WIDGET_HEIGHT : undefined}
          bodyHeight={!fullWidth ? MAX_CONTRACT_DETAILS_WIDGET_HEIGHT : undefined}
        >
          <TableWrapper>
            <Table>
              <tbody>
                <Row>
                  <Label>{t`gigCompliance.gigDetails.client`}</Label>
                  <Value>{contractDetails?.clientName || '-'}</Value>
                </Row>
                <Row>
                  <Label>{t`gigCompliance.gigDetails.gig`}</Label>
                  <Value>{contractDetails?.jobName || '-'}</Value>
                </Row>
                <Row>
                  <Label>{t`gigCompliance.gigDetails.networkComplianceDate`}</Label>
                  <Value>
                    {contractDetails
                      ? formatDate(
                          sub(convertBlockToDate(contractDetails?.snapshotBlock, timezoneName), {
                            minutes: 1,
                          }),
                          WEEK_DAY_TIME_FORMAT,
                        )
                      : ''}
                  </Value>
                </Row>
                <Row>
                  <Label>{t`gigCompliance.gigDetails.weekStart`}</Label>
                  <Value>
                    {contractDetails
                      ? formatDate(
                          add(convertBlockToDate(contractDetails?.weekStartBlock, timezoneName), { minutes: 1 }),
                          WEEK_DAY_TIME_FORMAT,
                        )
                      : ''}
                  </Value>
                </Row>
              </tbody>
            </Table>
          </TableWrapper>
        </Card>
      </Container>
    </StyledLoaderWrapper>
  );
};

export { ContractDetails };

const Container = styled.div`
  width: 100%;

  ${media.tablet} {
    margin: 16px 9px 0 9px;
    width: 25%;

    &.full-width {
      margin: 0;
      width: 100%;
    }
  }
`;

const StyledLoaderWrapper = styled(LoaderWrapper)<{ fullWidth: boolean | undefined }>`
  ${media.tablet} {
    width: ${({ fullWidth }) => (fullWidth ? '100%' : '25%')};
  }
`;

const TableWrapper = styled.div`
  padding: 15px 14px;
`;

const Table = styled.table`
  width: 100%;
`;

const Row = styled.tr`
  & > td {
    padding-top: 0.375rem;
    padding-bottom: 0.375rem;
  }
`;

const Label = styled.td`
  font-size: 12px;
  line-height: 18px;
  color: #5a5a5a;
  width: 40%;
  overflow: hidden;
`;

const Value = styled.td`
  font-size: 12px;
  line-height: 18px;
  color: #4b5563;
  width: 60%;
  overflow: hidden;
  padding-left: 15px;
`;
